import { ImageTool, MediaTemplateKind } from '@/constants/mediaTemplates';

export interface MediaTemplateFaq {
  question: string;
  answer: string;
}

export interface MediaTemplateSeoContent {
  intro: string;
  benefits: string[];
  useCases: string[];
  steps: string[];
  tips: string[];
  faqs: MediaTemplateFaq[];
}

/* ─────────────────────────────────────────────
   Per-template content overrides keyed by slug
   ───────────────────────────────────────────── */
const templateContent: Record<string, MediaTemplateSeoContent> = {
  /* ═══  VIDEO TEMPLATES  ═══ */

  // Video Agent Explainer looks (open /create-video with the look picked).
  'comic-explainer-video': {
    intro:
      'Turn any topic into a narrated explainer drawn as bold comic panels. The Video Agent researches your topic, writes the script, records the narration and builds every board: characters, speech balloons, icons and captions appear on the exact word the narrator says them.',
    benefits: [
      'Get a finished explainer from one idea: research, script, narration and every board are made for you.',
      'Bold panels, halftone dots and speech balloons make dry topics feel like a story.',
      'Make it up to 10 minutes long, with chapters, or as a vertical Short.',
    ],
    useCases: [
      'Explain a product, a process or a piece of history as a comic for YouTube, a course or a team update.',
      'Publish long explainers on YouTube with the title, description, hashtags and chapters written for you.',
      'Turn any chapter into a vertical Short, or translate the whole video with a native narrator.',
    ],
    steps: [
      'Open the template: the Video Agent opens with Explainer and the Comic look picked.',
      'Describe your topic in a sentence or a paragraph, or keep the example idea.',
      'Choose a regular video or a vertical Short, the length, a narrator and background music.',
      'Click Generate video, then edit any board in the editor and export in up to 4K.',
    ],
    tips: [
      'Ask for a cast of one or two characters so the speech balloons have someone to speak.',
      'The more you say about your audience and what they should learn, the better the script.',
      'If a board is not quite right, redo just that board in the editor and add a note on what to change.',
    ],
    faqs: [
      {
        question: 'How long can a Comic explainer be?',
        answer:
          'On paid plans, up to 10 minutes as a regular video with chapters, or 20 to 60 seconds as a vertical Short. A one-minute video takes about two minutes to make.',
      },
      {
        question: 'Do I need to write a script?',
        answer:
          'No. Describe your idea and the Video Agent researches the topic, writes the script and records the narration. You can also paste your notes or an outline into the idea box.',
      },
      {
        question: 'Can I edit the video after it is made?',
        answer:
          'Yes. It opens in the video editor, where you can change the text, icons and timing of any board, redo a board with a note, and export in up to 4K.',
      },
      {
        question: 'Can I make it in another language?',
        answer:
          'Yes. Pick the language before you generate, or translate a finished video: the Video Agent remakes it with a native narrator.',
      },
    ],
  },

  'riso-print-explainer-video': {
    intro:
      'Turn any topic into a narrated explainer printed in two bright inks on grainy paper. The Video Agent researches your topic, writes the script, records the narration and builds every board, with icons, charts and maps that appear on the exact word the narrator says them.',
    benefits: [
      'Get a finished explainer from one idea: research, script, narration and every board are made for you.',
      'Two bright inks and a grainy paper texture give every board the look of a printed zine.',
      'Make it up to 10 minutes long, with chapters, or as a vertical Short.',
    ],
    useCases: [
      'Make design-led explainers for brands, newsletters, museums and creative courses.',
      'Publish long explainers on YouTube with the title, description, hashtags and chapters written for you.',
      'Turn any chapter into a vertical Short, or translate the whole video with a native narrator.',
    ],
    steps: [
      'Open the template: the Video Agent opens with Explainer and the Riso Print look picked.',
      'Describe your topic in a sentence or a paragraph, or keep the example idea.',
      'Choose a regular video or a vertical Short, the length, a narrator and background music.',
      'Click Generate video, then edit any board in the editor and export in up to 4K.',
    ],
    tips: [
      'Topics with numbers work well: charts and timelines print in the same two inks.',
      'The more you say about your audience and what they should learn, the better the script.',
      'If a board is not quite right, redo just that board in the editor and add a note on what to change.',
    ],
    faqs: [
      {
        question: 'How long can a Riso Print explainer be?',
        answer:
          'On paid plans, up to 10 minutes as a regular video with chapters, or 20 to 60 seconds as a vertical Short. A one-minute video takes about two minutes to make.',
      },
      {
        question: 'Do I need to write a script?',
        answer:
          'No. Describe your idea and the Video Agent researches the topic, writes the script and records the narration. You can also paste your notes or an outline into the idea box.',
      },
      {
        question: 'Can I edit the video after it is made?',
        answer:
          'Yes. It opens in the video editor, where you can change the text, icons and timing of any board, redo a board with a note, and export in up to 4K.',
      },
      {
        question: 'Can I make it in another language?',
        answer:
          'Yes. Pick the language before you generate, or translate a finished video: the Video Agent remakes it with a native narrator.',
      },
    ],
  },

  'pixel-art-explainer-video': {
    intro:
      'Make a narrated explainer that plays like a retro 8-bit video game. The Video Agent researches your topic, writes the script, records the narration and builds every board: chapters become levels, and pixel characters and icons appear on the exact word the narrator says them.',
    benefits: [
      'Get a finished explainer from one idea: research, script, narration and every board are made for you.',
      'Levels, pixel fonts and game-style title cards turn a lesson into a quest.',
      'Make it up to 10 minutes long, with chapters, or as a vertical Short.',
    ],
    useCases: [
      'Teach coding, science or history to students and gamers, or explain a product launch with a playful twist.',
      'Publish long explainers on YouTube with the title, description, hashtags and chapters written for you.',
      'Turn any chapter into a vertical Short, or translate the whole video with a native narrator.',
    ],
    steps: [
      'Open the template: the Video Agent opens with Explainer and the Pixel Quest look picked.',
      'Describe your topic in a sentence or a paragraph, or keep the example idea.',
      'Choose a regular video or a vertical Short, the length, a narrator and background music.',
      'Click Generate video, then edit any board in the editor and export in up to 4K.',
    ],
    tips: [
      'Split a longer topic into chapters: each chapter opens with its own level card.',
      'The more you say about your audience and what they should learn, the better the script.',
      'If a board is not quite right, redo just that board in the editor and add a note on what to change.',
    ],
    faqs: [
      {
        question: 'How long can a Pixel Quest explainer be?',
        answer:
          'On paid plans, up to 10 minutes as a regular video with chapters, or 20 to 60 seconds as a vertical Short. A one-minute video takes about two minutes to make.',
      },
      {
        question: 'Do I need to write a script?',
        answer:
          'No. Describe your idea and the Video Agent researches the topic, writes the script and records the narration. You can also paste your notes or an outline into the idea box.',
      },
      {
        question: 'Can I edit the video after it is made?',
        answer:
          'Yes. It opens in the video editor, where you can change the text, icons and timing of any board, redo a board with a note, and export in up to 4K.',
      },
      {
        question: 'Can I make it in another language?',
        answer:
          'Yes. Pick the language before you generate, or translate a finished video: the Video Agent remakes it with a native narrator.',
      },
    ],
  },

  'stickman-explainer-video': {
    intro:
      'Turn any topic into a narrated explainer drawn in black marker on white paper. The Video Agent researches your topic, writes the script, records the narration and builds every board: a hand draws stick figures, line icons and headings on the exact word the narrator says them, then sweeps the page clean for the next board.',
    benefits: [
      'Get a finished explainer from one idea: research, script, narration and every board are made for you.',
      'Stick figures that talk, point, cheer and react make abstract ideas easy to follow.',
      'Make it up to 10 minutes long, with chapters, or as a vertical Short.',
    ],
    useCases: [
      'Explain habits, money, psychology or how something works, for a YouTube channel, a lesson or a team training.',
      'Publish long explainers on YouTube with the title, description, hashtags and chapters written for you.',
      'Turn any chapter into a vertical Short, or translate the whole video with a native narrator.',
    ],
    steps: [
      'Open the template: the Video Agent opens with Explainer and the Stickman look picked.',
      'Describe your topic in a sentence or a paragraph, or keep the example idea.',
      'Choose a regular video or a vertical Short, the length, a narrator and background music.',
      'Click Generate video, then edit any board in the editor and export in up to 4K.',
    ],
    tips: [
      'Name one or two characters in your idea, so the stick figures can act out the problem and the solution.',
      'The more you say about your audience and what they should learn, the better the script.',
      'If a board is not quite right, redo just that board in the editor and add a note on what to change.',
    ],
    faqs: [
      {
        question: 'How long can a Stickman explainer be?',
        answer:
          'On paid plans, up to 10 minutes as a regular video with chapters, or 20 to 60 seconds as a vertical Short. A one-minute video takes about two minutes to make.',
      },
      {
        question: 'Do I need to write a script?',
        answer:
          'No. Describe your idea and the Video Agent researches the topic, writes the script and records the narration. You can also paste your notes or an outline into the idea box.',
      },
      {
        question: 'Can I edit the video after it is made?',
        answer:
          'Yes. It opens in the video editor, where you can change the text, icons and timing of any board, redo a board with a note, and export in up to 4K.',
      },
      {
        question: 'Can I make it in another language?',
        answer:
          'Yes. Pick the language before you generate, or translate a finished video: the Video Agent remakes it with a native narrator.',
      },
    ],
  },

  'watercolor-explainer-video': {
    intro:
      'Turn a topic into a narrated storybook explainer. The Video Agent researches your topic, writes the script and records the narration, and every object and character on the boards is painted in watercolor just for your video, appearing on the exact word the narrator says it.',
    benefits: [
      'Get a finished explainer from one idea: research, script, narration and every board are made for you.',
      'Soft watercolor paintings on textured paper give your explainer the warmth of a picture book.',
      'Make it up to 10 minutes long, with chapters, or as a vertical Short.',
    ],
    useCases: [
      'Explain nature, science and life lessons for children, families, schools and storytelling channels.',
      'Publish long explainers on YouTube with the title, description, hashtags and chapters written for you.',
      'Turn any chapter into a vertical Short, or translate the whole video with a native narrator.',
    ],
    steps: [
      'Open the template: the Video Agent opens with Explainer and the Watercolor Storybook look picked.',
      'Describe your topic in a sentence or a paragraph, or keep the example idea.',
      'Choose a regular video or a vertical Short, the length, a narrator and background music.',
      'Click Generate video, then edit any board in the editor and export in up to 4K.',
    ],
    tips: [
      'Give your main character a name and a role in the idea, so the paintings keep them recognizable from board to board.',
      'The more you say about your audience and what they should learn, the better the script.',
      'If a board is not quite right, redo just that board in the editor and add a note on what to change.',
    ],
    faqs: [
      {
        question: 'How long can a Watercolor Storybook explainer be?',
        answer:
          'On paid plans, up to 10 minutes as a regular video with chapters, or 20 to 60 seconds as a vertical Short. A one-minute video takes about two minutes to make.',
      },
      {
        question: 'Do I need to write a script?',
        answer:
          'No. Describe your idea and the Video Agent researches the topic, writes the script and records the narration. You can also paste your notes or an outline into the idea box.',
      },
      {
        question: 'Can I edit the video after it is made?',
        answer:
          'Yes. It opens in the video editor, where you can change the text, icons and timing of any board, redo a board with a note, and export in up to 4K.',
      },
      {
        question: 'Can I make it in another language?',
        answer:
          'Yes. Pick the language before you generate, or translate a finished video: the Video Agent remakes it with a native narrator.',
      },
    ],
  },

  'clay-animation-explainer-video': {
    intro:
      'Make a narrated explainer with clay models on a set. The Video Agent researches your topic, writes the script and records the narration, and every object and character on the boards is modeled in clay just for your video, appearing on the exact word the narrator says it.',
    benefits: [
      'Get a finished explainer from one idea: research, script, narration and every board are made for you.',
      'Clay characters and props on a warm set make money, science and how-to topics friendly and memorable.',
      'Make it up to 10 minutes long, with chapters, or as a vertical Short.',
    ],
    useCases: [
      'Explain personal finance, health, science or onboarding topics in a friendly, hand-made style.',
      'Publish long explainers on YouTube with the title, description, hashtags and chapters written for you.',
      'Turn any chapter into a vertical Short, or translate the whole video with a native narrator.',
    ],
    steps: [
      'Open the template: the Video Agent opens with Explainer and the Claymation look picked.',
      'Describe your topic in a sentence or a paragraph, or keep the example idea.',
      'Choose a regular video or a vertical Short, the length, a narrator and background music.',
      'Click Generate video, then edit any board in the editor and export in up to 4K.',
    ],
    tips: [
      'Comparisons work well: two clay characters side by side make a choice or a trade-off easy to follow.',
      'The more you say about your audience and what they should learn, the better the script.',
      'If a board is not quite right, redo just that board in the editor and add a note on what to change.',
    ],
    faqs: [
      {
        question: 'How long can a Claymation explainer be?',
        answer:
          'On paid plans, up to 10 minutes as a regular video with chapters, or 20 to 60 seconds as a vertical Short. A one-minute video takes about two minutes to make.',
      },
      {
        question: 'Do I need to write a script?',
        answer:
          'No. Describe your idea and the Video Agent researches the topic, writes the script and records the narration. You can also paste your notes or an outline into the idea box.',
      },
      {
        question: 'Can I edit the video after it is made?',
        answer:
          'Yes. It opens in the video editor, where you can change the text, icons and timing of any board, redo a board with a note, and export in up to 4K.',
      },
      {
        question: 'Can I make it in another language?',
        answer:
          'Yes. Pick the language before you generate, or translate a finished video: the Video Agent remakes it with a native narrator.',
      },
    ],
  },

  'vintage-engraving-explainer-video': {
    intro:
      'Tell a story in the style of old encyclopedia plates. The Video Agent researches your topic, writes the script and records the narration, and every object and character on the boards is engraved just for your video, with maps and timelines that appear on the exact word the narrator says them.',
    benefits: [
      'Get a finished explainer from one idea: research, script, narration and every board are made for you.',
      'Fine engraved lines on aged paper give history, science and biography topics the authority of an old book.',
      'Make it up to 10 minutes long, with chapters, or as a vertical Short.',
    ],
    useCases: [
      'Explain history, inventions, biographies and classic science for documentaries, lessons and podcasts.',
      'Publish long explainers on YouTube with the title, description, hashtags and chapters written for you.',
      'Turn any chapter into a vertical Short, or translate the whole video with a native narrator.',
    ],
    steps: [
      'Open the template: the Video Agent opens with Explainer and the Vintage Engraving look picked.',
      'Describe your topic in a sentence or a paragraph, or keep the example idea.',
      'Choose a regular video or a vertical Short, the length, a narrator and background music.',
      'Click Generate video, then edit any board in the editor and export in up to 4K.',
    ],
    tips: [
      'History topics shine here: name places and years in your idea to get maps and timelines.',
      'The more you say about your audience and what they should learn, the better the script.',
      'If a board is not quite right, redo just that board in the editor and add a note on what to change.',
    ],
    faqs: [
      {
        question: 'How long can a Vintage Engraving explainer be?',
        answer:
          'On paid plans, up to 10 minutes as a regular video with chapters, or 20 to 60 seconds as a vertical Short. A one-minute video takes about two minutes to make.',
      },
      {
        question: 'Do I need to write a script?',
        answer:
          'No. Describe your idea and the Video Agent researches the topic, writes the script and records the narration. You can also paste your notes or an outline into the idea box.',
      },
      {
        question: 'Can I edit the video after it is made?',
        answer:
          'Yes. It opens in the video editor, where you can change the text, icons and timing of any board, redo a board with a note, and export in up to 4K.',
      },
      {
        question: 'Can I make it in another language?',
        answer:
          'Yes. Pick the language before you generate, or translate a finished video: the Video Agent remakes it with a native narrator.',
      },
    ],
  },

  'realtor-property-tour': {
    intro:
      'Turn any property listing photo into a polished video tour led by a virtual realtor. This template animates a still room image into a walking, talking presenter who introduces the unit and pitches its features &mdash; perfect for real estate agents, property managers, and rental platforms that need scroll-stopping listing videos.',
    benefits: [
      'Produce professional listing tour videos without hiring a videographer or on-camera talent.',
      'AI voiceover delivers your property pitch with clear narration and natural pacing.',
      'Generate dozens of variations by swapping the room photo and script for each unit.',
    ],
    useCases: [
      'Promote rental and sale listings on Instagram, TikTok, and YouTube Shorts with branded video tours.',
      'Add virtual realtor walkthroughs to property listing pages on your website or portal.',
      'Create multilingual property tours by adjusting the script for each target market.',
    ],
    steps: [
      'Upload a high-quality interior photo of the property as the starting frame.',
      'Edit the realtor script with your property name, location, price, and key selling points.',
      'Choose a duration between 8 and 15 seconds depending on your platform.',
      'Generate the video and review the realtor delivery before sharing or embedding.',
    ],
    tips: [
      'Use a bright, well-lit room photo so the virtual realtor blends naturally into the scene.',
      'Lead the script with the address, price, and one standout feature for the strongest hook.',
      'Keep the pitch under 30 words for cleaner narration and tighter lip-sync results.',
    ],
    faqs: [
      {
        question: 'Do I need a real realtor to appear in the video?',
        answer:
          'No. The AI generates the realtor presenter automatically and synthesizes lip-synced narration. You only need a photo of the property interior and a short script.',
      },
      {
        question: 'Can I customize the property details and price?',
        answer:
          'Yes. The script is fully editable &mdash; change the property name, address, district, monthly rent or sale price, bedrooms, bathrooms, and any other selling points to match your listing.',
      },
      {
        question: 'What aspect ratio works best for property listing videos?',
        answer:
          'For social platforms like Instagram Reels and TikTok, use a 9:16 vertical ratio. For listing portals and YouTube, a 16:9 horizontal format gives a more traditional real estate tour feel.',
      },
      {
        question: 'How long does the realtor tour video take to generate?',
        answer:
          'Generation typically takes 1&ndash;3 minutes depending on duration. The Grok Imagine model handles realistic motion, lip-sync, and ambient room tone in a single pass.',
      },
    ],
  },

  'property-furniture-flyover': {
    intro:
      'Showcase every piece of furniture in a property listing with a smooth cinematic fly-over. This template animates a still interior photo into a flowing camera movement that highlights each item in the room &mdash; ideal for real estate agents, interior designers, and home staging professionals who want to give viewers a richer sense of the space.',
    benefits: [
      'Add cinematic motion to flat listing photos without filming a real walkthrough.',
      'Highlight individual furniture pieces and design details that static images miss.',
      'Produce premium-looking property videos in minutes instead of hours.',
    ],
    useCases: [
      'Upgrade real estate listing pages with motion-rich previews that feel professionally shot.',
      'Promote interior design portfolios by spotlighting furniture choices and styling decisions.',
      'Showcase home staging work for property marketing and social media campaigns.',
    ],
    steps: [
      'Upload a wide-angle interior photo that captures the main furniture pieces in the room.',
      'Adjust the prompt if you want to emphasize specific items or change the camera pace.',
      'Set the duration to 10 seconds for a complete fly-over of the space.',
      'Generate the video and use it on listing pages, social posts, or property reels.',
    ],
    tips: [
      'Use a high-resolution photo with clear separation between furniture pieces for the smoothest motion.',
      'Bright, evenly lit rooms produce the most cinematic fly-over results &mdash; avoid harsh backlighting.',
      'Pair the fly-over with the Realtor Property Tour template for a complete listing video package.',
    ],
    faqs: [
      {
        question: 'What kind of room photos work best for the fly-over?',
        answer:
          'Wide-angle interior shots that show multiple furniture pieces at once produce the best results. Photos with good lighting, minimal clutter, and clear depth of field translate to the smoothest camera motion.',
      },
      {
        question: 'Can I use this template for empty rooms?',
        answer:
          'The fly-over works best when there is furniture to highlight. For empty rooms, consider the Realtor Property Tour template or pair this with a virtual staging step beforehand.',
      },
      {
        question: 'How is this different from a 360° room view?',
        answer:
          'A 360° view rotates around a fixed point, while the fly-over moves through the space and pauses on individual items. The fly-over feels more cinematic and is better suited to social media reels.',
      },
      {
        question: 'Can I add voiceover narration to the fly-over?',
        answer:
          'Yes. After generating the silent fly-over, you can layer in a narration track using the AI Text to Speech tool or pair it with the Realtor Property Tour template for a complete narrated walkthrough.',
      },
    ],
  },

  'ugc-product-review': {
    intro:
      'Turn any product into authentic-looking user-generated content. This template creates a selfie-style video where a presenter holds your product, speaks with accurate lip-sync, and delivers a natural review — no actors, no studio, no editing.',
    benefits: [
      'Get UGC-quality footage without hiring creators or waiting for deliverables.',
      'Lip-synced speech sounds natural and matches facial movement precisely.',
      'Swap the product, script, or presenter to produce dozens of variations fast.',
    ],
    useCases: [
      'Run A/B tested UGC ad creatives across TikTok, Instagram Reels, and YouTube Shorts.',
      'Produce unboxing and first-impression videos for ecommerce product pages.',
      'Create multilingual product demos by changing the script for each market.',
    ],
    steps: [
      'Upload a clear image of your product and a reference photo for the presenter.',
      'Write or adjust the review script — keep it conversational and under 30 seconds.',
      'Choose a 9:16 aspect ratio for vertical social formats.',
      'Generate the video and review lip-sync accuracy before publishing.',
    ],
    tips: [
      'Use a frontal, well-lit product image so the presenter can hold it naturally.',
      'Write the script in first person ("I just got this\u2026") for an authentic UGC feel.',
      'Keep sentences short — shorter phrases produce cleaner lip-sync results.',
    ],
    faqs: [
      {
        question: 'Do I need to hire a real person for the UGC video?',
        answer:
          'No. The AI generates the presenter from a reference photo and synthesizes lip-synced speech automatically. You can use any portrait image as the presenter.',
      },
      {
        question: 'Can I change the product being reviewed after generating?',
        answer:
          'Yes. Simply swap the product image and re-generate. The template keeps the same presenter and script structure, so you can iterate quickly across different products.',
      },
      {
        question: 'What video format and resolution does this template output?',
        answer:
          'The default output is 9:16 vertical video optimized for TikTok, Instagram Reels, and YouTube Shorts. Resolution depends on the AI model used, typically 720p or 1080p.',
      },
      {
        question: 'How realistic is the lip-sync?',
        answer:
          'The AI matches mouth movement to the spoken words with high accuracy. Shorter sentences and clear pronunciation in the script produce the most natural results.',
      },
    ],
  },

  'social-media-video-ad': {
    intro:
      'Create scroll-stopping product ads that combine realistic camera motion with AI-generated voiceover. This template takes a single product image and produces a polished short-form ad ready for paid social — no video editor, no voice actor required.',
    benefits: [
      'Go from product photo to finished ad creative in minutes instead of days.',
      'AI voiceover delivers your tagline with studio-quality clarity.',
      'Realistic camera movement makes the ad feel professionally shot.',
    ],
    useCases: [
      'Launch paid ad campaigns on Instagram, TikTok, and Facebook with multiple creative variants.',
      'Create product teasers and launch announcements for social feeds.',
      'Produce seasonal promotional content at high volume without a production team.',
    ],
    steps: [
      'Upload a high-resolution product photo on a clean background.',
      'Edit the voiceover script and tagline to match your brand voice.',
      'Set the aspect ratio to 9:16 for Stories/Reels or 16:9 for YouTube pre-roll.',
      'Generate several versions and pick the one with the strongest opening hook.',
    ],
    tips: [
      'Front-load the most eye-catching movement within the first 2 seconds to stop the scroll.',
      'Keep voiceover copy under 15 words — brevity performs better in paid social.',
      'Use a clean, isolated product shot for the sharpest camera motion results.',
    ],
    faqs: [
      {
        question: 'Can I use my own voiceover script?',
        answer:
          'Yes. The template includes a default script, but you can replace it with your own tagline, product name, and call-to-action. The AI voice will speak whatever text you provide.',
      },
      {
        question: 'What aspect ratios are supported?',
        answer:
          'The template supports 9:16 (vertical for Stories, Reels, TikTok), 16:9 (horizontal for YouTube), and 1:1 (square for feed posts). Choose the ratio that matches your ad placement.',
      },
      {
        question: 'How many ad variations can I create from one product photo?',
        answer:
          'There is no limit. Change the voiceover, camera motion style, or background each time to generate as many variations as you need for A/B testing.',
      },
      {
        question: 'Do I need a transparent product image?',
        answer:
          'A transparent background is not required, but a clean, well-lit product photo on a solid background produces the sharpest camera motion and most professional result.',
      },
    ],
  },

  'product-360-rotation': {
    intro:
      'Generate a smooth 360-degree rotation video from a single product photo. This template produces a clean, continuous spin that showcases every angle of your product — perfect for ecommerce listings, pitch decks, and social proof.',
    benefits: [
      'No turntable, studio, or photographer needed — just one product image.',
      'Smooth, consistent rotation that looks like professional product photography.',
      'Reuse the same video across your store, ads, and presentations.',
    ],
    useCases: [
      'Add 360\u00B0 spin videos to Shopify, Amazon, or WooCommerce product pages.',
      'Embed product rotation clips in investor decks and sales presentations.',
      'Share product showcase reels on Instagram and TikTok for launches.',
    ],
    steps: [
      'Upload a clean product photo — ideally shot on a white or neutral background.',
      'Review the default prompt and adjust if you need a specific rotation speed.',
      'Generate the video and verify the product maintains its shape through the full spin.',
      'Download and embed directly on your product page or social post.',
    ],
    tips: [
      'A centered, well-lit product shot with minimal shadows produces the cleanest rotation.',
      'Avoid images with complex reflective surfaces — matte products rotate more consistently.',
      'Use a square crop on the input image so the product stays centered throughout.',
    ],
    faqs: [
      {
        question:
          'Does the product need to be photographed from multiple angles?',
        answer:
          'No. The AI generates a full 360-degree rotation from a single front-facing product photo. It infers the back and sides based on the visible details.',
      },
      {
        question: 'Can I control the rotation speed?',
        answer:
          'Yes. You can adjust the prompt to request a slower or faster rotation. The default is a smooth, steady spin that completes one full revolution.',
      },
      {
        question: 'What types of products work best with this template?',
        answer:
          'Hard-surface products like electronics, bottles, packaging, and accessories produce the cleanest results. Products with fine details like fabric texture or transparent materials may need extra prompt refinement.',
      },
      {
        question: 'Can I use the 360 video on my Shopify store?',
        answer:
          'Yes. Download the video file and upload it directly to your product listing. It works as a standard MP4 video on any ecommerce platform.',
      },
    ],
  },

  'clay-avatar-podcast': {
    intro:
      'Create talking videos starring a clay-style avatar in a miniature podcast studio. This template combines a stylized 3D character with lip-synced speech, giving you a charming, Pixar-adjacent presenter for educational content, social clips, or brand storytelling.',
    benefits: [
      'A distinctive visual style that stands out from generic AI talking heads.',
      'Perfect lip-sync and natural expressions bring the clay character to life.',
      'No face reveal needed — the avatar represents your brand or persona.',
    ],
    useCases: [
      'Produce short explainer clips for YouTube, TikTok, or LinkedIn.',
      'Create a recurring podcast or series character for brand consistency.',
      'Build educational content for kids or casual audiences where a playful tone fits.',
    ],
    steps: [
      'Choose or upload a clay avatar image (the template provides a default studio scene).',
      'Select a voice from the available options or upload custom audio.',
      'Write or paste the script your avatar will speak.',
      'Generate the video and check lip-sync timing before publishing.',
    ],
    tips: [
      'Keep scripts under 60 seconds — short, punchy clips perform best on social.',
      'Choose a voice that matches the playful, approachable vibe of the clay art style.',
      'Front-load the key message in the first 5 seconds to hook viewers.',
    ],
    faqs: [
      {
        question: 'Can I use my own clay avatar design?',
        answer:
          'Yes. Upload any clay-style or 3D character image and the AI will animate it with lip-sync. The template also includes a default clay avatar if you want to start quickly.',
      },
      {
        question: 'What voices are available for the avatar?',
        answer:
          "You can choose from a library of AI voices in multiple languages and styles, or upload your own audio recording. The AI will sync the avatar's mouth movement to whichever audio source you provide.",
      },
      {
        question: 'Is this suitable for professional or corporate content?',
        answer:
          'The clay avatar style works well for approachable, friendly content. For more formal presentations, consider using the Make Yourself a YouTuber or News Anchor templates instead.',
      },
    ],
  },

  'viral-dance-motion-control': {
    intro:
      'Make any character perform a viral dance by transferring motion from a reference video. Upload a dance clip and a character image, and the AI maps every move onto your subject — timing, choreography, and energy preserved.',
    benefits: [
      'Transfer real choreography onto any character without manual animation.',
      'The character keeps its original appearance while performing the exact moves.',
      'Produce trending content formats without hiring dancers or filming.',
    ],
    useCases: [
      'Create trending dance challenge content with brand mascots or product characters.',
      'Animate illustrated characters for social media entertainment accounts.',
      'Produce engaging short-form content for TikTok and Instagram Reels at scale.',
    ],
    steps: [
      'Upload a reference dance video with clear, visible movement.',
      'Upload or generate an image of the character you want to animate.',
      'Review the motion-matching prompt and adjust if needed.',
      'Generate and compare the output against the reference to verify timing accuracy.',
    ],
    tips: [
      'Choose a reference video with a single person and a clean background for best motion transfer.',
      'Full-body character images produce more accurate results than close-up shots.',
      'Shorter dance clips (5\u201310 seconds) tend to transfer more reliably than longer ones.',
    ],
    faqs: [
      {
        question: 'Can I use any dance video as the motion reference?',
        answer:
          'Yes. Upload any video with visible body movement. The AI extracts the motion data and maps it onto your character. Single-person clips with minimal camera movement produce the best transfer.',
      },
      {
        question: 'Does the character need to be a real person?',
        answer:
          "No. The character can be an illustration, a 3D render, a cartoon, or a real photo. The AI preserves the character's appearance while applying the dance motion.",
      },
      {
        question: 'How long can the dance clip be?',
        answer:
          'Clips of 5 to 10 seconds produce the most reliable results. Longer clips can work but may have less consistent motion matching. For best quality, trim to the strongest section of the dance.',
      },
    ],
  },

  'valentine-kiss-cam': {
    intro:
      'Recreate the classic stadium kiss-cam moment with AI. Upload two face photos and the template generates a realistic arena scene — heart frame, cheering crowd, and a sweet on-screen moment between the two subjects.',
    benefits: [
      'A fun, personalized video gift without needing to be at a live event.',
      'Realistic stadium lighting and crowd atmosphere sell the illusion.',
      'Both subjects keep their original appearance with accurate face preservation.',
    ],
    useCases: [
      "Create personalized Valentine's Day or anniversary video gifts.",
      'Produce shareable social media content around sports events and holidays.',
      'Build engagement content for dating apps or relationship-focused brands.',
    ],
    steps: [
      'Upload two clear, front-facing reference photos — one for each person.',
      'Review the default prompt and adjust the stadium setting if desired.',
      'Generate the kiss-cam video and check face accuracy for both subjects.',
      'Download and share directly or add to a greeting card or social post.',
    ],
    tips: [
      'Use well-lit, forward-facing portraits — side profiles reduce face accuracy.',
      'Photos with neutral expressions work best since the AI adds the smile and lean-in.',
      'High-resolution reference images (at least 512\u00D7512) produce sharper face details.',
    ],
    faqs: [
      {
        question: 'How many reference photos do I need?',
        answer:
          'Two photos — one for each person in the kiss-cam scene. Each photo should be a clear, front-facing portrait.',
      },
      {
        question: 'Will the faces look accurate?',
        answer:
          'Yes. The template uses reference-image-based generation that preserves facial features with high accuracy. Well-lit, front-facing photos produce the best identity match.',
      },
      {
        question: 'Can I customize the stadium or event setting?',
        answer:
          'Yes. The default prompt creates a classic stadium kiss-cam with a red heart frame. You can modify the prompt to change the venue, frame style, or crowd energy.',
      },
    ],
  },

  'make-yourself-a-youtuber': {
    intro:
      'Create talking-head videos using your own face or a chosen avatar. This template turns a portrait and a script into a camera-ready presenter — complete with natural lip-sync, head movement, and expressions. Ideal for channels, courses, and daily content.',
    benefits: [
      'Publish consistent video content without filming, lighting, or editing.',
      'Natural lip-sync and head motion make the result feel genuinely recorded.',
      'Build a recognizable on-screen presence without appearing on camera yourself.',
    ],
    useCases: [
      'Launch a YouTube channel or podcast with AI-presented episodes.',
      'Create course content and tutorial walkthroughs at scale.',
      'Produce daily news digests or commentary videos without studio time.',
    ],
    steps: [
      'Upload a front-facing portrait or choose from 100+ pre-built avatars.',
      'Write the script or paste content you want the presenter to deliver.',
      "Select a voice and adjust pacing to match your channel's tone.",
      'Generate the video and verify lip-sync before uploading to your platform.',
    ],
    tips: [
      'A portrait with a neutral expression and eye contact gives the most natural starting point.',
      'Break long scripts into shorter segments for more consistent lip-sync quality.',
      'Add pauses in the script (with commas or periods) to give the delivery a natural rhythm.',
    ],
    faqs: [
      {
        question:
          'Can I use my own face or do I have to use a pre-built avatar?',
        answer:
          'Both options work. Upload your own portrait photo for a personalized presenter, or choose from 100+ pre-built avatars if you prefer not to use your own likeness.',
      },
      {
        question: 'How long can the video be?',
        answer:
          'Talking videos can be up to 5 minutes long, and a typed script can be up to 2,500 characters. For longer content, generate multiple segments and combine them in a video editor.',
      },
      {
        question: 'What languages are supported?',
        answer:
          'The AI voices support 40+ languages. You can write your script in any supported language and the avatar will deliver it with matching lip-sync.',
      },
      {
        question: 'Can I use this for commercial YouTube content?',
        answer:
          'Yes, on a paid plan: the videos you generate can be published commercially. Many creators use this template for regular YouTube uploads, online courses, and podcast video versions. Videos made on the Free plan are for non-commercial use only.',
      },
    ],
  },

  'news-anchor': {
    intro:
      'Turn a single portrait into a realistic news anchor broadcasting live. The template generates studio lighting, a scrolling news ticker, and professional delivery — all from one image and a prompt. Perfect for satire, education, or branded content.',
    benefits: [
      'Full broadcast-quality look from just one portrait image.',
      'Professional studio lighting and ticker graphics are generated automatically.',
      'Natural speaking gestures and expressions bring the anchor to life.',
    ],
    useCases: [
      'Create mock news segments for educational or entertainment content.',
      'Produce branded "breaking news" announcements for product launches.',
      'Build satirical or comedy content in a familiar news format.',
    ],
    steps: [
      'Upload a front-facing portrait — the AI places the subject into a news studio.',
      'Adjust the prompt if you want specific background elements or ticker text.',
      'Generate the video and review the studio lighting and anchor delivery.',
      'Export and add your own lower-third graphics or voiceover if needed.',
    ],
    tips: [
      'Formal or semi-formal clothing in the portrait photo reinforces the news anchor look.',
      'A neutral facial expression lets the AI generate natural speaking motion.',
      'The default prompt works well — small adjustments to "scrolling news ticker" text can personalize it.',
    ],
    faqs: [
      {
        question: 'Does the AI add a news studio background automatically?',
        answer:
          'Yes. The template prompt instructs the AI to place the subject into a professional TV studio with appropriate lighting, background screens, and a scrolling news ticker.',
      },
      {
        question: 'Can I control what the news ticker says?',
        answer:
          'The ticker content is generated by the AI based on the prompt. You can add specific text to the prompt to influence it, or overlay your own ticker in post-production for full control.',
      },
      {
        question: 'What type of portrait works best?',
        answer:
          'A front-facing portrait with neutral expression and professional attire. The AI handles the rest — studio lighting, depth of field, and speaking animation.',
      },
    ],
  },

  'cinematic-transition': {
    intro:
      'Create a fluid, continuous camera move that seamlessly connects two images. The AI generates a cinematic transition where colors, textures, and environments blend as if both scenes exist in the same physical space — no cuts, no effects, just one unbroken shot.',
    benefits: [
      'Seamless scene transitions that feel like a single continuous camera take.',
      'Consistent lighting, depth, and perspective throughout the movement.',
      'Works with any pair of images — interiors, landscapes, product shots, or portraits.',
    ],
    useCases: [
      'Create cinematic intros and outros for YouTube videos and brand films.',
      'Build immersive scene transitions for real estate or travel content.',
      'Produce before/after reveal videos for design, renovation, or product projects.',
    ],
    steps: [
      'Upload a start image and an end image — these define the two scenes to connect.',
      'Review the transition prompt and adjust camera movement direction if needed.',
      'Generate the video and check that the mid-point blend feels smooth and natural.',
      'Use the output as a standalone clip or edit it into a larger video project.',
    ],
    tips: [
      'Choose images with similar color temperatures for the smoothest blending.',
      'Images with depth (foreground + background elements) produce more cinematic camera paths.',
      'Describe the specific transition path in the prompt (e.g., "zoom through the doorway") for more intentional results.',
    ],
    faqs: [
      {
        question: 'Can I use any two images for the transition?',
        answer:
          'Yes. The AI can connect any pair of images — indoor to outdoor, close-up to wide shot, or completely different environments. Images with visual similarities (color, lighting) produce the smoothest results.',
      },
      {
        question: 'How long is the transition video?',
        answer:
          'The default output is typically 5 to 10 seconds depending on the AI model. This is usually enough for a seamless transition between scenes in a larger project.',
      },
      {
        question: 'Can I control the camera path between the two images?',
        answer:
          'Yes. The prompt accepts specific camera movement instructions like "zoom through the window" or "pan left into the second scene." More specific directions produce more intentional transitions.',
      },
      {
        question: 'Is this suitable for professional video editing workflows?',
        answer:
          'Yes. The output is a standard video file that can be imported into Premiere Pro, DaVinci Resolve, Final Cut, or any editor. Many creators use these transitions as connecting shots between scenes.',
      },
    ],
  },

  'street-to-space-zoom': {
    intro:
      'Generate a breathtaking continuous zoom that starts at street level and pulls back all the way to a cosmic space view. The camera steadily rises from bustling city life to reveal the full planet and stars beyond — all in one seamless shot.',
    benefits: [
      'A visually stunning zoom-out that captures the scale from human to cosmic.',
      'Smooth, steady dolly-out motion with natural lighting transitions.',
      'An attention-grabbing opener or closer for any video project.',
    ],
    useCases: [
      'Create powerful intro or outro sequences for documentaries and brand films.',
      'Produce awe-inspiring social media content that stops the scroll.',
      'Build visual metaphors for presentations about scale, growth, or global reach.',
    ],
    steps: [
      'Upload a street-level city image as the starting point.',
      'Review the zoom-out prompt and adjust the pacing or midpoint details if desired.',
      'Generate the video and check that the transition between urban and cosmic views is smooth.',
      'Use as a standalone piece or trim to the segment that fits your project best.',
    ],
    tips: [
      'Night-time city shots with visible lights create a more dramatic transition to the starfield.',
      'The default prompt includes an uplifting score cue — adjust or remove it depending on your needs.',
      'A high-resolution starting image ensures the street-level detail holds up during the initial close-up.',
    ],
    faqs: [
      {
        question: 'Do I need separate images for the street and space views?',
        answer:
          'No. You only upload a single street-level starting image. The AI generates the entire zoom-out sequence — from city to sky to space — based on the prompt instructions.',
      },
      {
        question: 'Can I start from a location other than a city street?',
        answer:
          'Yes. The template works with any starting location. You can begin from a rooftop, a park, a rural scene, or any ground-level image. Adjust the prompt to match your starting point.',
      },
      {
        question: 'How long is the zoom-out video?',
        answer:
          'The default output is 5 to 10 seconds. The zoom covers a lot of visual distance in that time, making it feel expansive and dramatic even at short durations.',
      },
    ],
  },

  'change-character-in-scene': {
    intro:
      'Replace the character in any video with a new one while keeping the original motion, timing, and scene intact. Upload a reference video and a character image, and the AI swaps the subject while preserving every gesture and movement.',
    benefits: [
      'Swap characters in existing footage without re-shooting or rotoscoping.',
      'Original camera motion, timing, and scene context are fully preserved.',
      'The new character retains its appearance, outfit, and identity throughout.',
    ],
    useCases: [
      'Repurpose existing video content with different characters for new campaigns.',
      'Create variations of the same scene for A/B testing ad creatives.',
      'Produce character-driven content without filming live actors.',
    ],
    steps: [
      'Upload a reference video showing the motion you want to transfer.',
      'Upload an image of the new character to place into the scene.',
      'Review the motion-control prompt and adjust if needed.',
      'Generate and compare the output against the reference for motion accuracy.',
    ],
    tips: [
      'Reference videos with a single subject and minimal camera shake produce the best results.',
      'Full-body character images match the motion more accurately than headshots.',
      'Keep the reference video under 10 seconds for the most reliable character transfer.',
    ],
    faqs: [
      {
        question: 'Does this work with any video as the motion source?',
        answer:
          'Yes. Upload any video with visible human movement. The AI extracts the motion and applies it to your new character. Videos with a single subject and stable camera produce the best results.',
      },
      {
        question: 'Will the new character look exactly like my uploaded image?',
        answer:
          "The AI preserves the character's key visual features — face, outfit, body type, and style. Minor variations may occur during complex movements, but overall identity consistency is high.",
      },
      {
        question: 'Can I use animated or illustrated characters?',
        answer:
          'Yes. The character image can be a photo, illustration, 3D render, or any visual style. The motion transfer works across all character types.',
      },
    ],
  },

  'match-motion-control': {
    intro:
      "Recreate a reference video's exact timing, movement, and choreography with a completely new character. Upload a routine video and a character photo, and the AI reproduces every gesture while keeping the new subject's appearance unchanged.",
    benefits: [
      'Exact motion matching — every head tilt, hand gesture, and step is reproduced.',
      "The new character's identity, clothing, and style are fully preserved.",
      'No animation skills needed — the reference video drives everything.',
    ],
    useCases: [
      'Create influencer-style "get ready with me" or routine videos without filming.',
      'Produce product demo videos where a character performs specific actions.',
      'Repurpose trending video formats with your own brand character or mascot.',
    ],
    steps: [
      'Upload the reference video that contains the motion sequence you want to replicate.',
      'Upload a clear image of the character who will perform the routine.',
      'Review the motion-matching prompt — the default preserves timing and identity.',
      "Generate and review frame-by-frame to verify the character's appearance is consistent.",
    ],
    tips: [
      'Choose a reference video with clean, deliberate movements — fast, erratic motion is harder to transfer.',
      "The character image should match the reference video's framing (full-body for full-body shots).",
      'Solid or simple backgrounds in the reference video improve transfer quality.',
    ],
    faqs: [
      {
        question:
          'What is the difference between this and the Change Character template?',
        answer:
          'Both use motion control, but this template emphasizes exact timing and choreography matching — ideal for routines, dances, and step-by-step actions. The Change Character template is better for general scene replacement.',
      },
      {
        question: 'Does the audio from the reference video transfer too?',
        answer:
          'The motion is transferred, but audio is not automatically carried over. You can add the original audio or new audio in post-production.',
      },
      {
        question: 'Can I match motion from a professionally filmed video?',
        answer:
          'Yes. Professional footage with steady camera work and clear subject visibility actually produces the best motion matching results.',
      },
    ],
  },

  /* ═══  IMAGE TEMPLATES  ═══ */

  'professional-headshot': {
    intro:
      "Transform any casual photo into a polished, studio-quality professional headshot. The AI preserves your exact facial features while adding professional lighting, a clean background, smart-casual styling, and the kind of crisp detail you'd get from an 85mm portrait lens.",
    benefits: [
      "100% face identity preservation — it's unmistakably you, just studio-polished.",
      'Professional studio lighting, shallow depth of field, and crisp eye detail.',
      'Skip the photographer — go from phone selfie to LinkedIn-ready portrait in minutes.',
    ],
    useCases: [
      'Update your LinkedIn, company directory, or personal website profile photo.',
      'Create consistent team headshots for an "About Us" page without booking a group shoot.',
      'Generate professional author photos for publications, bios, or press kits.',
    ],
    steps: [
      'Upload a clear, front-facing photo where your face is well-lit and unobstructed.',
      'Review the prompt — the default creates a smart-casual blazer look with studio lighting.',
      'Adjust clothing, background, or lighting style in the prompt if you want a different look.',
      'Generate a few variations and pick the one that captures your best angle.',
    ],
    tips: [
      'A front-facing photo with direct eye contact produces the sharpest, most natural result.',
      'Avoid photos with heavy shadows on the face — even, natural light works best as input.',
      'If you want a specific outfit, describe it in the prompt instead of relying on the default blazer.',
    ],
    faqs: [
      {
        question: 'Will the headshot actually look like me?',
        answer:
          'Yes. The template uses face identity preservation, which means your exact facial features, skin tone, and distinguishing characteristics are maintained. The AI enhances lighting, background, and styling — not your face.',
      },
      {
        question: 'Can I use a selfie as the input photo?',
        answer:
          'Yes. A front-facing selfie with good lighting works well. The AI compensates for typical selfie issues like wide-angle lens distortion and uneven lighting.',
      },
      {
        question: 'Can I generate headshots for my entire team?',
        answer:
          'Yes. Upload individual photos for each team member and run the template separately for each person. This is a popular use case for creating consistent "About Us" pages.',
      },
      {
        question: 'What background options are available?',
        answer:
          'The default is a soft, neutral studio background with bokeh. You can change it to any setting by editing the prompt — office environment, outdoor, solid color, or branded backdrop.',
      },
    ],
  },

  'vogue-cover-portrait': {
    intro:
      'Generate a realistic Vogue magazine cover from any face photo. The AI creates a full editorial layout — masthead, cover lines, paparazzi cameras, luxury styling, and a confident pose — while preserving your facial features with pinpoint accuracy.',
    benefits: [
      'A magazine-quality cover image with realistic editorial layout and typography.',
      'Face identity is preserved while the AI adds professional styling and pose.',
      'Paparazzi cameras and flashbulbs in the scene add dynamic, high-fashion energy.',
    ],
    useCases: [
      'Create eye-catching profile images and social media content.',
      'Design personalized gifts — magazine covers make memorable birthday and event presents.',
      'Produce fashion-forward visual content for beauty and lifestyle brands.',
    ],
    steps: [
      'Upload a clear face photo — the AI will handle styling, pose, and layout.',
      'Review the prompt — the default includes a Vogue masthead, luxury jewelry, and paparazzi.',
      'Adjust the outfit, jewelry, or pose description if you want a different editorial direction.',
      'Generate and choose the version with the best cover composition and facial accuracy.',
    ],
    tips: [
      'Front-facing photos with neutral expressions give the AI the most room to add the cover pose.',
      'High-resolution input images (at least 1024px) produce sharper facial details on the cover.',
      'If you want a specific fashion brand or style, mention it in the prompt for more targeted results.',
    ],
    faqs: [
      {
        question: 'Will the cover have real Vogue branding?',
        answer:
          'The AI generates a Vogue-style cover layout with a masthead that closely resembles the editorial format. It aims for the look and feel of a fashion magazine cover rather than an exact brand replica.',
      },
      {
        question: 'Can I create covers in other magazine styles?',
        answer:
          'Yes. Edit the prompt to specify a different magazine aesthetic — GQ, Elle, Vanity Fair, or any editorial style. The AI will adjust the layout, typography, and mood accordingly.',
      },
      {
        question: 'Do I need a professional photo to start?',
        answer:
          'No. The template works with casual photos, selfies, and portraits. The AI adds all the fashion-forward elements — you just need a clear, well-lit face photo.',
      },
    ],
  },

  'product-background-change': {
    intro:
      'Swap the background of any product photo while keeping the product completely untouched — every label, color, shadow, and reflection stays exactly as it was. Drop in a sunset sky, a marble surface, or any scene, and the product looks naturally placed.',
    benefits: [
      'The product is pixel-perfect — no warping, no color shifts, no missing labels.',
      'Photorealistic lighting match between the new background and the product.',
      'Generate dozens of background variations from a single product shot.',
    ],
    useCases: [
      'Create seasonal or campaign-specific product imagery without re-shooting.',
      'Test different lifestyle backgrounds for ecommerce A/B testing.',
      'Produce social media content with varied settings from one studio product photo.',
    ],
    steps: [
      'Upload a product photo — clean studio shots with minimal shadows work best.',
      'Edit the prompt to describe your desired background (e.g., "marble countertop with morning light").',
      'Generate the image and verify the product edges blend naturally with the new scene.',
      'Download and use directly on your store, ad, or social post.',
    ],
    tips: [
      "Describe the background lighting to match the product's existing light direction for realism.",
      'Be specific about the surface the product sits on — "wooden table" gives better results than just "wood."',
      'Products with transparent elements (glass bottles) need extra attention — verify reflections in the output.',
    ],
    faqs: [
      {
        question: 'Will the product labels and branding stay intact?',
        answer:
          'Yes. The template is specifically designed to preserve every detail of the product — text, logos, labels, colors, and proportions remain exactly as they appear in the original photo.',
      },
      {
        question: 'How many different backgrounds can I generate?',
        answer:
          'There is no limit. Change the prompt to describe any environment — beach, kitchen counter, studio, outdoor table, shelf — and generate a new version each time.',
      },
      {
        question:
          'Does it work with products that have reflections or transparency?',
        answer:
          'It works, but products with glass, reflections, or transparent elements require more careful review. Check that reflections in the output match the new background environment.',
      },
      {
        question: 'Can I use this for Amazon or Shopify product listings?',
        answer:
          'Yes. The output is a standard image file that meets platform requirements. Many sellers use this to create lifestyle versions of their product photos without additional photography.',
      },
    ],
  },

  'chinese-new-year-card': {
    intro:
      'Turn any portrait into a cute, toy-style \u606D\u559C\u53D1\u8D22 greeting card with Chinese traditional decorations. The AI creates a cartoon figure wearing traditional costume, surrounded by tangerines, yuanbao, and fishes — then makes it speak with lip-synced animation.',
    benefits: [
      'A unique, personalized greeting card that combines cute toy style with traditional Chinese elements.',
      'Lip-synced talking animation turns the card into a shareable video message.',
      'The cartoon figure preserves the likeness of the person in the uploaded photo.',
    ],
    useCases: [
      'Send personalized Chinese New Year video greetings to family, friends, and clients.',
      'Create social media content for Lunar New Year marketing campaigns.',
      'Produce festive greeting animations for messaging apps and email newsletters.',
    ],
    steps: [
      'Upload a clear face photo of the person you want to turn into a toy figure.',
      'Review the prompt — the default includes \u606D\u559C\u53D1\u8D22 text, traditional costume, and lucky decorations.',
      'Adjust the name or blessing text at the bottom of the card if desired.',
      'Generate and share the animated greeting directly or add it to a video message.',
    ],
    tips: [
      'A front-facing photo with a smile produces the most charming toy-style result.',
      'Keep the background of the input photo simple so the AI focuses on the face.',
      'The red and gold color scheme is baked in — if you want different accent colors, specify them.',
    ],
    faqs: [
      {
        question: 'Can I add my own blessing text to the card?',
        answer:
          'Yes. The default card includes \u606D\u559C\u53D1\u8D22 at the top and a name placeholder at the bottom. You can change both by editing the prompt.',
      },
      {
        question: 'Does the card animate or is it a still image?',
        answer:
          'The card becomes an animated talking video. The toy-style figure speaks with lip-synced animation, making it a shareable video greeting rather than a static image.',
      },
      {
        question: 'Can I use this for other holidays or just Chinese New Year?',
        answer:
          'The template is designed for Chinese New Year with traditional elements. For other holidays, you can modify the prompt to change the theme, decorations, and text.',
      },
    ],
  },

  /* ═══  WORKFLOW TEMPLATES  ═══ */

  'workflow-talking-vegetables': {
    intro:
      'Create fun, Pixar-style animated clips of talking vegetables sharing surprising facts. This workflow template chains image generation, character animation, and voice synthesis into one automated pipeline — perfect for educational and entertainment content.',
    benefits: [
      'A fully automated multi-step workflow — from character design to final animated video.',
      'Pixar-adjacent art style that appeals to both kids and adult audiences.',
      'Each vegetable character is unique, expressive, and speaks with natural lip-sync.',
    ],
    useCases: [
      "Produce educational content for children's YouTube channels or classroom materials.",
      'Create entertaining social media series around food, nutrition, or science facts.',
      'Build episodic content with recurring vegetable characters for brand mascots.',
    ],
    steps: [
      'Open the Talking Vegetables workflow template in the workflow editor.',
      'Customize the vegetable character, fact script, and voice selection.',
      'Run the workflow — the pipeline handles image generation, animation, and audio automatically.',
      'Review the final animated clip and publish to your chosen platform.',
    ],
    tips: [
      'Short, surprising facts (under 15 seconds) produce the most shareable clips.',
      'Use an enthusiastic, upbeat voice selection to match the playful visual style.',
      'Create a series with different vegetables to build a recurring content format.',
    ],
    faqs: [
      {
        question: 'What does the workflow automate?',
        answer:
          'The workflow chains three steps: generating a Pixar-style vegetable character image, animating it with lip-sync, and adding voice synthesis. You customize the inputs, and the pipeline handles the rest.',
      },
      {
        question: 'Can I choose which vegetable character to create?',
        answer:
          'Yes. You can specify any vegetable (or fruit) in the workflow settings. Each character is generated fresh with unique Pixar-style details.',
      },
      {
        question: "Is this suitable for children's content?",
        answer:
          "Yes. The Pixar-adjacent art style is specifically designed to be friendly and appealing for younger audiences, making it ideal for educational YouTube channels, classroom resources, and kids' apps.",
      },
    ],
  },

  'workflow-virtual-try-on': {
    intro:
      'Generate a model image, dress them in your clothing items using AI editing, and animate the result into a promotional video. This workflow automates the entire virtual try-on pipeline from model generation to final video output.',
    benefits: [
      'Full pipeline from model generation to dressed image to animated video.',
      'No photography studio or model booking required — everything is AI-generated.',
      'Quickly produce multiple outfit combinations from the same base model.',
    ],
    useCases: [
      'Create product preview videos for online fashion and apparel stores.',
      'Generate lookbook content showing multiple outfits on the same model.',
      'Produce social media marketing clips for clothing launches and seasonal collections.',
    ],
    steps: [
      'Open the Virtual Try-On workflow and upload images of your clothing items.',
      'Generate or upload a model image that fits your brand aesthetic.',
      'Run the workflow — the AI dresses the model and animates the result automatically.',
      'Review the final video and adjust model pose or clothing placement if needed.',
    ],
    tips: [
      'Flat-lay or mannequin clothing photos produce the most accurate try-on results.',
      'Choose a model image with a neutral pose so the clothing drapes naturally.',
      'Generate multiple outfit combinations to build a complete lookbook in one session.',
    ],
    faqs: [
      {
        question: 'What kind of clothing images work best?',
        answer:
          'Flat-lay photos (clothing laid flat on a surface) and mannequin shots produce the most accurate results. Product images with clean backgrounds also work well.',
      },
      {
        question: 'Can I use my own model photos instead of AI-generated ones?',
        answer:
          'Yes. You can upload your own model photos or use AI-generated models. The workflow supports both approaches.',
      },
      {
        question: 'Does the workflow produce a video or just a still image?',
        answer:
          'Both. The workflow generates a dressed model image and then animates it into a short promotional video, giving you both static and video assets.',
      },
    ],
  },

  'workflow-interior-design': {
    intro:
      'Transform a 2D floor plan into a 3D isometric maquette, generate photorealistic room renders, and create an animated video tour — all in one automated workflow. From blueprint to walkthrough in minutes.',
    benefits: [
      'Full pipeline from 2D floor plan to 3D visualization to animated video tour.',
      'Photorealistic room renders with accurate lighting, materials, and proportions.',
      'No 3D modeling software needed — the AI handles the entire transformation.',
    ],
    useCases: [
      'Present interior design concepts to clients with realistic room visualizations.',
      'Create property listing videos for real estate marketing.',
      'Produce before/after renovation previews for home improvement content.',
    ],
    steps: [
      'Upload a 2D floor plan or room layout sketch.',
      'Configure the design style, materials, and color palette in the workflow settings.',
      'Run the workflow to generate 3D renders and the animated video tour.',
      'Review and share the walkthrough video with clients or on listing platforms.',
    ],
    tips: [
      'Clear, high-contrast floor plans produce the most accurate 3D translations.',
      'Specify a design style (e.g., "Scandinavian minimalist") for cohesive material choices.',
      'Add furniture preferences to the prompt for more tailored room staging.',
    ],
    faqs: [
      {
        question: 'Can I use a hand-drawn sketch as the input?',
        answer:
          'Yes. The workflow accepts both professional floor plans and hand-drawn sketches. Clearer lines and labels produce more accurate 3D translations.',
      },
      {
        question: 'What design styles are supported?',
        answer:
          'Any style you describe — modern, Scandinavian, industrial, rustic, mid-century, minimalist, or maximalist. Specify the style in the workflow settings and the AI will apply matching materials, colors, and furniture.',
      },
      {
        question: 'Can I use the output for client presentations?',
        answer:
          'Yes. The photorealistic renders and video tour are designed for professional presentations. Many interior designers and real estate agents use this workflow for client proposals.',
      },
    ],
  },

  'workflow-consistent-characters': {
    intro:
      'Create scenes with consistent characters across multiple AI models. Upload reference images and generate coherent variations — same character, different scenes, different styles — while maintaining identity across every output.',
    benefits: [
      'Character identity stays consistent across different AI models and scenes.',
      'Generate varied compositions while keeping the same recognizable characters.',
      'Compare outputs from multiple models to find the best style for your project.',
    ],
    useCases: [
      'Build character-driven narratives with a consistent protagonist across scenes.',
      'Create storyboards and visual sequences for animation or comic projects.',
      'Produce marketing materials with a consistent brand character or mascot.',
    ],
    steps: [
      'Upload reference images of the character(s) you want to keep consistent.',
      'Define the scenes and settings for each variation in the workflow.',
      'Run the workflow across multiple AI models to generate scene variations.',
      'Review outputs and select the model and style that best fits your project.',
    ],
    tips: [
      'Provide 2\u20133 reference angles of the character for stronger identity preservation.',
      'Keep scene descriptions detailed but let the character description stay anchored to the reference.',
      'Test with Flux and SDXL models for stylistic variety while maintaining consistency.',
    ],
    faqs: [
      {
        question: 'How consistent are the characters across different scenes?',
        answer:
          "High consistency when using reference images. The workflow anchors the character's identity to your reference photos, so facial features, clothing, and body type remain recognizable across all generated scenes.",
      },
      {
        question: 'Can I use multiple characters in the same scene?',
        answer:
          'Yes. Upload reference images for each character and the workflow can generate scenes with multiple consistent characters interacting.',
      },
      {
        question: 'Which AI models produce the most consistent characters?',
        answer:
          'Models with strong reference-image support (like Flux) tend to produce the most consistent characters. The workflow lets you compare results across models to find what works best.',
      },
    ],
  },

  'workflow-brand-storyboard': {
    intro:
      'Create a professional designer mockup series from a single product image. This workflow generates 9 high-end variations — hero shots, close-ups, lifestyle setups, and editorial compositions — ready for branding portfolios and marketing decks.',
    benefits: [
      '9 distinct, portfolio-grade compositions from one product photo.',
      'Professional mockup quality — hero shots, detail close-ups, and lifestyle scenes.',
      'Consistent brand aesthetic across all variations for cohesive presentations.',
    ],
    useCases: [
      'Build a complete visual branding package for investor decks and pitch materials.',
      'Create product photography suites for ecommerce listings and catalogs.',
      'Produce social media content libraries with varied compositions in one session.',
    ],
    steps: [
      'Upload a clean, high-resolution product image as the starting point.',
      'Configure the brand style, color palette, and mood in the workflow settings.',
      'Run the workflow to generate 9 variations across different composition styles.',
      'Curate the best shots for your portfolio, deck, or marketing materials.',
    ],
    tips: [
      'A well-lit product photo on a white or transparent background gives the most versatile results.',
      "Define your brand's color palette in the settings for visually cohesive outputs.",
      'Use the hero shot for key placements and the detail close-ups for supporting materials.',
    ],
    faqs: [
      {
        question: 'What types of compositions does the workflow generate?',
        answer:
          'The 9 variations include hero shots, product close-ups, lifestyle scenes, editorial layouts, and environmental compositions. Each is styled to work together as a cohesive brand package.',
      },
      {
        question: 'Can I customize the brand colors and style?',
        answer:
          "Yes. The workflow settings let you define your brand's color palette, mood, and visual direction. All 9 outputs will follow these guidelines for a consistent look.",
      },
      {
        question: 'Is one product photo really enough?',
        answer:
          'Yes. The AI infers product details, materials, and context from a single high-quality photo. A clean, well-lit studio shot gives the AI the most information to work with.',
      },
    ],
  },

  'workflow-ai-animated-short-film': {
    intro:
      'Turn a single story idea into a complete 15-second animated short film. This workflow takes your character references and story prompt and generates the full production around it &mdash; character sheets, a 12-shot cinematic storyboard, and the final animated video, all visually consistent from the first frame to the last.',
    benefits: [
      'Go from story idea to a finished animated short film in minutes, not days.',
      'Consistent characters, environment, and color palette across every scene and shot.',
      'A full production pipeline &mdash; character sheets, storyboard, and final video &mdash; in a single workflow.',
    ],
    useCases: [
      'Produce short animated films for social media, ads, or pitch reels.',
      'Prototype animated stories and pitch decks for studios, publishers, and agencies.',
      'Create personalized animated shorts for kids, family memories, or branded storytelling.',
    ],
    steps: [
      'Upload character reference images and write a short description for each character and setting.',
      'Add your story prompt describing the action, mood, and ending of the 15-second short.',
      'Run the workflow to generate character sheets and a 12-panel cinematic storyboard.',
      'Render the final animated video with consistent characters, environment, and lighting.',
    ],
    tips: [
      'Use clean, well-lit character references with a consistent art style for the strongest results.',
      'Describe the environment, props, and lighting in each character description &mdash; the AI uses these to keep visuals coherent across scenes.',
      'Keep the story prompt cinematic: describe camera moves, beats, and the emotional ending you want.',
    ],
    faqs: [
      {
        question: 'How long does the final animated short film run?',
        answer:
          'The default workflow produces a 15-second animated short with 12 cinematic shots. You can adjust the duration and shot count in the storyboard and video nodes if you want a shorter or longer film.',
      },
      {
        question: 'Can I use my own characters and story?',
        answer:
          'Yes. Upload your own character images and write a short description of each character, setting, and story. The workflow will generate the character sheets, storyboard, and animated video around your assets.',
      },
      {
        question: 'What art styles work best for the animated short?',
        answer:
          'Stop-motion, needle-felted, claymation, hand-drawn, and 3D animation styles all work well. Describe the style explicitly in each character description so the workflow keeps the visual language consistent across every shot.',
      },
      {
        question: 'Do I need any animation or video editing skills?',
        answer:
          'No. The workflow handles character design, storyboarding, and animation end-to-end. You only need a story idea, character references, and short descriptions &mdash; the AI does the rest.',
      },
    ],
  },

  'workflow-sports-broadcast-fan-cameo': {
    intro:
      "Drop a single portrait into the world's biggest sporting events as if caught by a live broadcast camera. This workflow generates nine fan-cutaway scenes — NFL, MLB, KBO, Premier League, F1 paddock, NBA, cricket, Wimbledon, and volleyball — then animates four of them as short live-broadcast clips using four different video models.",
    benefits: [
      'Place yourself courtside, in the stands, or in the paddock across nine major sports without leaving your chair.',
      'Identity is preserved exactly while wardrobe, lighting, scoreboard graphics, and crowds match each sport.',
      'Compare four leading video models — Veo 3.1, Happy Horse, Seedance, and Kling — on the same scene.',
    ],
    useCases: [
      'Create viral fan-cam style posts for social media.',
      'Surprise sports-fan friends or family with personalized broadcast clips.',
      'Mock up sponsorship, fan-engagement, or campaign concepts for sports brands.',
    ],
    steps: [
      'Upload a clear, well-lit portrait of yourself.',
      'Run the workflow to generate the nine sports broadcast fan cutaways.',
      'Tweak any scene by clicking Settings on the corresponding Image to Image node.',
      'Animate your favorite stills with the Image to Video nodes to compare different models.',
    ],
    tips: [
      'Front-facing portraits with even lighting preserve identity best across every sport.',
      'Edit the prompt on any node to swap teams, attire, or stadium to match your favorite club.',
      'Use the example video prompt as a starting point and adjust camera moves to match the energy you want.',
    ],
    faqs: [
      {
        question: 'How realistic do the fan cutaways look?',
        answer:
          'The generated frames are designed to look like live TV broadcast captures — authentic scoreboards, stadium lighting, crowd context, and broadcast color grading. The realism depends on the source portrait; clear, front-facing photos produce the strongest results.',
      },
      {
        question: 'Can I change the team or sport shown in each scene?',
        answer:
          'Yes. Click Settings on any Image to Image node and edit the prompt to swap the team jersey, league, stadium, or camera angle. Then re-run that node.',
      },
      {
        question: 'Why are four scenes animated with different video models?',
        answer:
          'Each Image to Video node uses a different model (Veo 3.1, Happy Horse, Seedance 2.0 Fast, and Kling 3.0 Standard) so you can compare motion quality, camera behavior, and style on the same source frame.',
      },
      {
        question: 'Can I animate the other scenes too?',
        answer:
          'Yes. Drag a new Image to Video node onto the canvas, connect any Image to Image result to it, choose a model, and run the workflow.',
      },
    ],
  },

  'workflow-singapore-knitted': {
    intro:
      'Transform images of Singapore landmarks into knitted fabric textures and animate them with letter-by-letter knitting effects. This workflow produces charming, handcraft-style videos that turn iconic architecture into cozy textile art.',
    benefits: [
      'A unique handcraft-to-digital art style that feels warm and distinctive.',
      'Animated knitting effects bring the textile transformation to life frame by frame.',
      'Iconic landmarks are instantly recognizable even in the knitted style.',
    ],
    useCases: [
      'Create social media content celebrating Singapore culture and landmarks.',
      'Produce event and tourism marketing materials with a distinctive visual hook.',
      'Build arts-and-crafts themed content for textile, fashion, or lifestyle brands.',
    ],
    steps: [
      'Open the Singapore Knitted City workflow and select the landmark images.',
      'Configure the knitting style, yarn colors, and animation speed.',
      'Run the workflow to generate the textile transformation and letter animation.',
      'Review the animated output and export for social media or presentations.',
    ],
    tips: [
      'Landmarks with strong geometric shapes (Marina Bay Sands, Esplanade) translate best to knitted textures.',
      'Warm, saturated yarn colors produce the most visually appealing results.',
      'Keep animations under 15 seconds for maximum social media engagement.',
    ],
    faqs: [
      {
        question: 'Can I use landmarks from other cities?',
        answer:
          'Yes. While the template is designed for Singapore landmarks, you can upload any architectural image. Adjust the prompt to match the new location and the knitting transformation will apply the same textile style.',
      },
      {
        question: 'What is the letter-by-letter knitting animation?',
        answer:
          'The animation shows the landmark image being "knitted" into existence stitch by stitch, as if someone is crafting it in real time. It creates an engaging reveal effect.',
      },
      {
        question: 'Can I customize the yarn colors?',
        answer:
          'Yes. The workflow settings let you specify yarn colors, thickness, and knitting pattern style to match your brand or creative direction.',
      },
    ],
  },
};

/* ─────────────────────────────────────────────
   Main export — returns template-specific
   content when available, otherwise builds a
   contextual fallback from the template data.
   ───────────────────────────────────────────── */
export const getMediaTemplateSeoContent = (
  template: ImageTool,
  kind: MediaTemplateKind,
): MediaTemplateSeoContent => {
  if (template.slug && templateContent[template.slug]) {
    return templateContent[template.slug];
  }

  // Contextual fallback using the template's own title & description
  const title = template.title;
  const intro = `${template.description} The ${title} template gives you a ready-to-use starting point \u2014 adjust the prompt to fit your project, generate, and publish.`;

  const benefits = [
    `Start with a proven setup tailored for ${title.toLowerCase()} instead of writing prompts from scratch.`,
    'Reduce guesswork and get consistent, high-quality outputs on the first try.',
    'Fully customizable \u2014 adjust every detail to match your brand and creative direction.',
  ];

  const useCases =
    kind === 'video'
      ? [
          'Create social media videos for product launches and marketing campaigns.',
          'Produce short-form content for TikTok, Reels, and YouTube Shorts.',
          'Build ad creatives and branded video content at scale.',
        ]
      : [
          'Create product imagery for ecommerce stores and landing pages.',
          'Generate concept art and visual directions for campaigns.',
          'Produce social graphics and cover images for content marketing.',
        ];

  const steps = [
    `Open the ${title} template and review the pre-configured settings.`,
    'Replace the default subject or product with your own assets and details.',
    kind === 'video'
      ? 'Adjust camera movement, pacing, and aspect ratio to fit your platform.'
      : 'Tune the visual style, composition, and lighting to match your brand.',
    'Generate several versions, compare results, and keep the strongest output.',
  ];

  const tips = [
    'Be specific about the subject, setting, and mood for the most consistent results.',
    'Run 2\u20133 prompt variations and compare outputs before selecting your final version.',
    kind === 'video'
      ? 'Include clear pacing and transition cues in the prompt for smoother motion.'
      : 'Specify lighting direction and camera framing for stronger visual quality.',
  ];

  const faqs: MediaTemplateFaq[] = [
    {
      question: `What is the ${title} template?`,
      answer: `The ${title} template is a pre-configured AI ${kind} generation setup that gives you a proven starting point. Instead of writing a prompt from scratch, you start with an optimized configuration and customize it to fit your specific project.`,
    },
    {
      question: `Do I need any technical skills to use this template?`,
      answer: `No. The template is ready to use out of the box. Upload your assets, adjust the prompt if needed, and generate. No design, video editing, or AI expertise required.`,
    },
    {
      question: `Can I customize the output to match my brand?`,
      answer: `Yes. Every part of the template \u2014 the prompt, settings, and input assets \u2014 is fully editable. Adjust colors, style, tone, and subject matter to align with your brand guidelines.`,
    },
    {
      question: `How many ${kind}s can I generate from this template?`,
      answer: `There is no limit on the number of variations. Each generation uses credits from your account, and you can create as many versions as needed to find the perfect result.`,
    },
  ];

  return { intro, benefits, useCases, steps, tips, faqs };
};
